/**
 * ASSIGNEE-SEARCH-001 — Searchable assignee dropdown (UI-only enhancement).
 * Pinned entries (Unassigned / Me / No change) stay on top; all other names
 * are sorted A–Z with a case-insensitive, partial-match search box inside.
 * Drop-in controlled replacement for the previous native <select>s — emits the
 * exact same values, so assignment permissions/logic are untouched.
 */
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ChevronDown, Search, Check, Loader2, RefreshCw } from 'lucide-react';

export const SearchableAssigneeSelect = ({
  value,
  onChange,
  options = [],          // [{ id, label }]
  pinned = [],           // [{ value, label }] — always visible, top of list
  placeholder = 'Select Assignee...',
  disabled = false,
  testId = 'assignee-select',
  buttonClassName = 'text-sm px-3 py-2 rounded border border-gray-300 bg-white hover:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[200px]',
  // TYPING-PROCESSOR-LIST-001 — roster lifecycle so empty states are unambiguous
  listState = null,      // { loading, error, onOpen, onRetry, emptyLabel }
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const onOpenRef = useRef(null);
  onOpenRef.current = listState?.onOpen;

  useEffect(() => {
    if (open) onOpenRef.current?.();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDocClick); document.removeEventListener('keydown', onKey); };
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
    else setQuery('');
  }, [open]);

  const sorted = useMemo(
    () => [...options].sort((a, b) => (a.label || '').localeCompare(b.label || '', undefined, { sensitivity: 'base' })),
    [options]
  );
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(o => (o.label || '').toLowerCase().includes(q));
  }, [sorted, query]);

  const selectedLabel =
    pinned.find(p => String(p.value) === String(value))?.label ||
    options.find(o => String(o.id) === String(value))?.label ||
    null;

  const pick = (v) => { onChange(v); setOpen(false); };

  const hasOptions = options.length > 0;
  const isLoading = !!listState?.loading;
  const hasError = !!listState?.error;
  const q = query.trim();
  const renderEmptyState = () => {
    if (!hasOptions && isLoading) {
      return <div className="px-3 py-2 text-xs text-gray-500 flex items-center gap-2" data-testid={`${testId}-loading`}><Loader2 className="h-3.5 w-3.5 animate-spin" />Loading processors…</div>;
    }
    if (!hasOptions && hasError) {
      return (
        <div className="px-3 py-2 text-xs text-red-600 flex items-center justify-between gap-2" data-testid={`${testId}-error`}>
          <span>Couldn't load processors</span>
          <button type="button" onClick={() => listState?.onRetry?.()} className="inline-flex items-center gap-1 font-semibold text-blue-700 hover:underline" data-testid={`${testId}-retry`}>
            <RefreshCw className="h-3 w-3" />Retry
          </button>
        </div>
      );
    }
    if (!hasOptions && !q) {
      return <div className="px-3 py-2 text-xs text-gray-400" data-testid={`${testId}-empty`}>{listState?.emptyLabel || 'No assignees available'}</div>;
    }
    if (filtered.length === 0) {
      return <div className="px-3 py-2 text-xs text-gray-400" data-testid={`${testId}-no-match`}>No matching names</div>;
    }
    return null;
  };
  const staleNote = hasOptions && hasError ? (
    <div className="px-3 py-1.5 text-[11px] text-amber-700 bg-amber-50 border-t border-amber-100 flex items-center justify-between gap-2" data-testid={`${testId}-stale-note`}>
      <span>Showing last known list</span>
      <button type="button" onClick={() => listState?.onRetry?.()} className="font-semibold text-blue-700 hover:underline" data-testid={`${testId}-retry`}>Retry</button>
    </div>
  ) : (hasOptions && isLoading ? (
    <div className="px-3 py-1 text-[11px] text-gray-400 border-t border-gray-100" data-testid={`${testId}-refreshing`}>Refreshing…</div>
  ) : null);

  const Item = ({ v, label, isPinned }) => {
    const active = String(v) === String(value ?? '');
    return (
      <button
        type="button"
        role="option"
        aria-selected={active}
        onClick={() => pick(v)}
        className={`w-full text-left px-3 py-1.5 text-sm flex items-center justify-between hover:bg-blue-50 ${active ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-800'} ${isPinned ? 'font-medium' : ''}`}
        data-testid={`${testId}-option-${v || 'placeholder'}`}
      >
        <span className="truncate">{label}</span>
        {active && <Check className="h-3.5 w-3.5 text-blue-600 shrink-0" />}
      </button>
    );
  };

  return (
    <div ref={rootRef} className="relative inline-block">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen(o => !o)}
        className={`${buttonClassName} flex items-center justify-between gap-2 disabled:opacity-50 disabled:cursor-not-allowed`}
        data-testid={testId}
        aria-expanded={open}
        aria-haspopup="listbox"
        title={selectedLabel || placeholder}
      >
        <span className={`truncate ${selectedLabel ? 'text-gray-900' : 'text-gray-500'}`}>
          {selectedLabel || placeholder}
        </span>
        <ChevronDown className="h-3.5 w-3.5 text-gray-400 shrink-0" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[220px] bg-white border border-gray-200 rounded-md shadow-lg" data-testid={`${testId}-panel`}>
          <div className="flex items-center gap-1.5 px-2 py-1.5 border-b border-gray-100">
            <Search className="h-3.5 w-3.5 text-gray-400 shrink-0" />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name…"
              className="w-full text-sm outline-none placeholder:text-gray-400"
              data-testid={`${testId}-search`}
            />
          </div>
          <div className="max-h-64 overflow-y-auto py-1" role="listbox">
            {pinned.map(p => <Item key={`pin-${p.value}`} v={p.value} label={p.label} isPinned />)}
            {pinned.length > 0 && filtered.length > 0 && <div className="my-1 border-t border-gray-100" />}
            {filtered.map(o => <Item key={o.id} v={o.id} label={o.label} />)}
            {renderEmptyState()}
          </div>
          {staleNote}
        </div>
      )}
    </div>
  );
};

export default SearchableAssigneeSelect;
